
import { useState, useEffect } from 'react';
import { Interview } from '@/types/interview';

export const useViewInterviewDialog = (
  interview: Interview | null,
  isOpen: boolean,
  onPublish: (interviewId: string) => Promise<void>,
  onSendToFeeder: (interviewId: string) => Promise<void>,
  publishingId: string | null,
  sendingToFeederId: string | null
) => {
  const [error, setError] = useState<string | null>(null);
  const [dialogKey, setDialogKey] = useState(Date.now());
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(interview?.photoUrl);
  
  // Confirmation dialog states
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [isFeederConfirmOpen, setIsFeederConfirmOpen] = useState(false);

  // Force re-render when dialog opens or interview changes to ensure images are refreshed
  useEffect(() => {
    if (isOpen && interview) {
      const newKey = Date.now();
      console.log('[ViewInterviewDialog] Forcing re-render with new key:', newKey);
      setDialogKey(newKey);
      
      // Update photoUrl state from the interview
      if (interview.photoUrl !== photoUrl) {
        console.log('[ViewInterviewDialog] Updating photoUrl state:', {
          previous: photoUrl,
          new: interview.photoUrl
        });
        setPhotoUrl(interview.photoUrl);
      }
    }
  }, [isOpen, interview, photoUrl]);

  useEffect(() => {
    if (interview && isOpen) {
      console.log('[ViewInterviewDialog] Dialog opened with interview:', {
        id: interview.id,
        name: interview.intervieweeName,
        hasPhotoUrl: !!interview.photoUrl,
        photoUrl: interview.photoUrl || 'No photo URL',
        dialogKey
      });
    }
  }, [interview, isOpen, dialogKey]);

  const handleSendToFeeder = async () => {
    if (interview) {
      setError(null); // Clear any previous errors
      try {
        console.log('[ViewInterviewDialog] Sending to feeder:', interview.id);
        await onSendToFeeder(interview.id);
      } catch (error) {
        console.error("[ViewInterviewDialog] Error in handleSendToFeeder:", error);
        setError("Kunne ikke sende til køen. Prøv igjen.");
        // We don't close the dialog on error so the user can try again
        throw error; // Re-throw so the parent component knows there was an error
      }
    }
  };

  const handlePublish = async () => {
    if (interview) {
      setError(null); // Clear any previous errors
      try {
        console.log('[ViewInterviewDialog] Publishing interview:', interview.id);
        await onPublish(interview.id);
      } catch (error) {
        console.error("[ViewInterviewDialog] Error in handlePublish:", error);
        setError("Kunne ikke publisere. Prøv igjen.");
        // We don't close the dialog on error so the user can try again
        throw error; // Re-throw so the parent component knows there was an error
      }
    }
  };

  const isSendingToFeeder = interview && sendingToFeederId === interview.id;
  const isPublishing = interview && publishingId === interview.id;
  const isDisabled = publishingId !== null || sendingToFeederId !== null;

  return {
    error,
    dialogKey,
    photoUrl,
    isPublishConfirmOpen,
    isFeederConfirmOpen,
    setIsPublishConfirmOpen,
    setIsFeederConfirmOpen,
    handleSendToFeeder,
    handlePublish,
    isSendingToFeeder,
    isPublishing,
    isDisabled
  };
};
